// Source HitWallSpline (terrain.cpp:4086): one-sided planar sweep plus endpoint
// cylinders. Serialized Y stores scan bounds and is never wall height.
export function sweepInfiniteWalls(walls, position, movement, radius) {
  if (!Array.isArray(walls) || ![position?.x, position?.z, movement?.x, movement?.z, radius].every(Number.isFinite) || radius <= 0) throw new Error('Invalid wall sweep.');
  let hit = null, best = 1;
  const length = Math.hypot(movement.x, movement.z);
  function accept(time, nx, nz, wall, segment, penetrating) {
    if (!(time < best) || !Number.isFinite(nx) || !Number.isFinite(nz)) return;
    best = time; hit = { time: Math.max(0, time), unclampedTime: time, normal: { x: nx, y: 0, z: nz }, wall, segment, penetrating };
  }
  for (const wall of walls) for (let segment = 0; segment + 1 < wall.points.length; segment++) {
    const a = wall.points[segment], b = wall.points[segment + 1];
    const px = position.x, pz = position.z, mx = movement.x, mz = movement.z;
    if (!(Math.abs(px - a[0]) < 64 && Math.abs(pz - a[2]) < 64)) continue;
    const dx = b[0] - a[0], dz = b[2] - a[2], edgeLength = Math.hypot(dx, dz);
    if (!edgeLength) continue;
    const nx = dz / edgeLength, nz = -dx / edgeLength;
    const start = (px - a[0]) * nx + (pz - a[2]) * nz - radius;
    const end = (px + mx - a[0]) * nx + (pz + mz - a[2]) * nz - radius;
    if ((end < 0 || start < 0) && start > -radius) {
      const time = start < 0 ? start : start / (start - end);
      const x = start < 0 ? px - nx * start - nx * radius : px + mx * time - nx * radius;
      const z = start < 0 ? pz - nz * start - nz * radius : pz + mz * time - nz * radius;
      if ((x - a[0]) * dx + dz * (z - a[2]) >= -0.00005 && -dx * (x - b[0]) - (z - b[2]) * dz >= -0.00005)
        accept(time, nx, nz, wall.index, segment, start <= 0);
    }
    const ex = a[0] - px, ez = a[2] - pz, distanceSquared = ex * ex + ez * ez;
    if (!((radius + 0.005 + length) ** 2 > distanceSquared)) continue;
    const dirX = length ? mx / length : 0, dirZ = length ? mz / length : 0, along = dirX * ex + dirZ * ez;
    let endpoint = false;
    if (along >= 0) {
      const sideX = a[0] - (px + dirX * along), sideZ = a[2] - (pz + dirZ * along);
      if (sideX * sideX + sideZ * sideZ <= (radius + 0.0005) ** 2) {
        const distance = along - Math.sqrt(radius * radius - sideX * sideX - sideZ * sideZ);
        if (distance >= -0.0005 && length * length >= distance * distance) {
          const time = length && distance ? Math.max(0, distance / length) : 0;
          if (!(best > time)) continue;
          const normalX = px + dirX * distance - a[0], normalZ = pz + dirZ * distance - a[2], size = Math.hypot(normalX, normalZ);
          accept(time, normalX / size, normalZ / size, wall.index, segment, false); endpoint = true;
        }
      }
    }
    if (!endpoint && (radius + 0.0005) ** 2 >= distanceSquared) {
      const distance = Math.sqrt(distanceSquared);
      if (distance > 0) accept(distance - radius - 0.0005, -ex / distance, -ez / distance, wall.index, segment, true);
    }
  }
  return hit;
}
